/* ==========================================================================
   Bluefin A/B — telemetry and attribution.  Loaded with defer.
   Full rationale: docs/QA-2026-09-15.md

   RULE ZERO: telemetry must never break the purchase path. Every emit is
   wrapped so a gtag/dataLayer failure cannot reach the /cart/add wrappers.

   TAG FORMAT — split on ":": test, arm, then flags in any order.
       fit_img_001:b            saw the gallery, counts
       fit_img_001:b:noview     never opened a test product page -> excluded
       fit_img_001:b:unstable   assignment did not persist -> excluded
       fit_img_001:b:noswap     the page's gallery did not match the payload,
                                so no swap could happen; decided the same way
                                in BOTH arms -> excluded

   CONSENT: nothing is sent without it, but see the note by watchConsent —
   an early "false" means undecided, not refused.
   ========================================================================== */
(function () {
  'use strict';

  var AB = window.BluefinAB;
  if (!AB) return;               /* no test running */

  var SEEN_KEY = '_bf_ab_seen_' + AB.test;

  /* Declared up here, not down in section 3 where they are used, because
     markExposed/markSwapFailed below can call emit() and emit() reads them.
     Today every caller is late enough that it does not matter; a `var` read
     before its assignment is undefined, and `queue.length` on undefined throws
     inside emit's own catch, so the event would vanish without trace. Cheap to
     make impossible instead of relying on call order. */
  var consent = null;            /* null = undecided, true = yes, false = no */
  var queue = [];
  var MAX_QUEUE = 20;

  /* --- 1. Has this visitor ever seen a test gallery? --------------------- */

  function readSeen() {
    try { if (window.localStorage.getItem(SEEN_KEY) === '1') return true; } catch (e) {}
    try {
      if (new RegExp('(?:^|;\\s*)' + SEEN_KEY + '=1').test(document.cookie)) return true;
    } catch (e) {}
    return false;
  }

  function markSeen() {
    try { window.localStorage.setItem(SEEN_KEY, '1'); } catch (e) {}
    try { document.cookie = SEEN_KEY + '=1;path=/;max-age=7776000;SameSite=Lax'; } catch (e) {}
  }

  /* inTest is set by ab-test-gallery.liquid, on a test product page only. */
  if (AB.inTest) markSeen();
  var seen = AB.inTest || readSeen();
  var FIELD = 'properties[_ab]';

  /* `seen` is read from storage at load, and storage can change under a page
     that stays open: a second tab opens a product page, or the browser brings
     this page back from its back/forward cache. Reading it once stamped those
     shoppers :noview on a collection-page quick-add although they had seen the
     gallery -- the same "computed once" shape as three earlier bugs. So it is
     re-read whenever it matters, and the forms are re-tagged when the page
     comes back into view. */
  function isSeen() {
    if (!seen && readSeen()) seen = true;
    return seen;
  }

  function promoteSeen() {
    if (!seen) {
      seen = true;
      markSeen();
      retag();
    }
  }

  /* Set by the swap itself when it ran and matched nothing. Read at load
     because on a product page the inline swap runs BEFORE this deferred file,
     so it cannot call a function that does not exist yet. */
  var swapFailed = !!AB.swapFailed;

  /* Computed on demand, never frozen at load. A visitor can land on a
     collection page (seen = false) and only then open a quick-view, which is a
     real exposure. Freezing the tag stamped those buyers :noview and the
     analysis dropped them, although they had seen the gallery. */
  function value() {
    return AB.test + ':' + AB.arm
         + (AB.stable ? '' : ':unstable')
         + (swapFailed ? ':noswap' : '')
         + (isSeen() ? '' : ':noview');
  }

  function retag() {
    try {
      var v = value();
      document.querySelectorAll('[name="' + FIELD + '"]').forEach(function (i) { i.value = v; });
    } catch (e) {}
  }

  /* A page whose markup does not match the payload shows its own pictures,
     whatever the visitor's arm. The swap decides that in BOTH arms (see
     ab-test-gallery.liquid), and this page view is then left out of all three
     counts alike: the tag gets :noswap, no ab_exposure is emitted for it, and
     no ab_add_to_cart either. ab_noswap is emitted only so the failure can be
     seen and counted per arm -- it must be zero, and equal across arms if not.

     Two earlier versions got this wrong in opposite directions. Detected in
     arm B only, the exclusion was one-sided and biased the comparison. And
     subtracting ab_noswap sessions from arm B's exposures while its
     add-to-carts stayed in the numerator inflated arm B's rate: 5% of failures
     gave "B WINS" in 11% of simulated A/As. Not emitting the events in the
     first place removes the need for any subtraction. */
  var noswapEmitted = false;
  function emitNoswap() {
    if (noswapEmitted) return;
    noswapEmitted = true;
    emit('ab_noswap');
  }

  AB.markSwapFailed = function () {
    if (swapFailed) return;
    swapFailed = true;
    promoteSeen();          /* they did open a test gallery: not :noview */
    retag();
    emitNoswap();
  };

  /* Called by the swap, wherever it runs. Two separate jobs, and they must not
     be conflated: promote the visitor to "seen" (persistent, drives :noview),
     and count THIS page view as an exposure (per page, drives the denominator).

     A quick-view is a real gallery view. Until this existed, those visitors
     could emit ab_add_to_cart with no ab_exposure behind it: a numerator with
     no denominator, inflating whichever arm sends more people through the
     modal. That is not lost data, it is a biased comparison. */
  var exposed = false;
  AB.markExposed = function () {
    promoteSeen();
    /* A page view already flagged :noswap is excluded as a whole -- its forms
       carry the flag -- so a later good gallery on the same page (a second
       quick-view) must not count it back in. The reverse order, good first and
       failed later, leaves one exposure with its add-to-carts suppressed: a
       small dilution, the same in both arms. */
    if (!exposed && !swapFailed) {
      exposed = true;
      emit('ab_exposure');
    }
  };

  /* --- 2. Attribution: line item property on every add-to-cart form ------ */

  function tagForm(form) {
    try {
      if (!form || form.__bfAb) return;
      if (!/\/cart\/add/.test(form.getAttribute('action') || '')) return;
      form.__bfAb = true;
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = FIELD;
      input.value = value();
      form.appendChild(input);
    } catch (e) {}
  }

  function tagAllForms() {
    try { document.querySelectorAll('form[action*="/cart/add"]').forEach(tagForm); } catch (e) {}
  }

  tagAllForms();
  document.addEventListener('DOMContentLoaded', tagAllForms);

  if (window.MutationObserver) {
    try {
      new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          var added = records[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            if (n.nodeType !== 1) continue;
            if (n.tagName === 'FORM') tagForm(n);
            else if (n.querySelectorAll) n.querySelectorAll('form[action*="/cart/add"]').forEach(tagForm);
          }
        }
      }).observe(document.documentElement, { childList: true, subtree: true });
    } catch (e) {}
  }

  /* --- 3. Consent -------------------------------------------------------- */

  /* A "false" here means "has not said yes YET", not "said no" — the banner
     is usually still on screen when this runs, and ~89% then accept. Reading
     that first false as a refusal threw away half of all exposures.
     So nothing is ever discarded for a no: events queue in memory and are
     sent only if a yes actually arrives. Queuing transmits nothing. */

  function askPrivacyApi() {
    try {
      var api = window.Shopify && window.Shopify.customerPrivacy;
      if (api && typeof api.analyticsProcessingAllowed === 'function') {
        return api.analyticsProcessingAllowed();
      }
    } catch (e) {}
    return undefined;            /* API not loaded yet */
  }

  function grant() {
    if (consent === true) return;
    consent = true;
    queue.forEach(send);
    queue.length = 0;
  }

  function refuse() {            /* no for now. Keep the queue: the visitor may
                                    still accept, and nothing leaves memory
                                    until grant() actually sends it. */
    consent = false;
  }

  (function watchConsent() {
    try {
      /* Registered unconditionally: the decision usually arrives AFTER us. */
      document.addEventListener('visitorConsentCollected', function (e) {
        if (e && e.detail && e.detail.analyticsAllowed) grant(); else refuse();
      });

      var tries = 0;
      (function poll() {
        if (consent === true) return;
        if (askPrivacyApi() === true) { grant(); return; }   /* stored yes */
        if (++tries > 240) return;   /* give up polling; the listener stays */
        setTimeout(poll, 250);
      })();
    } catch (e) { refuse(); }
  })();

  /* --- 4. GA4 telemetry (diagnostic, not decision) ----------------------- */

  /* window.gtag is defined by other scripts (Pandectes, the Google app), not
     by the theme, and not necessarily before this runs. For a visitor whose
     "yes" is already stored, grant() fires on the first poll: measured on a
     copy of the product page, the queue was flushed at 1.36 s and gtag first
     existed at 2.00 s. The event went into dataLayer only -- which the Google
     tag on this store does not forward -- and never reached GA4. So the gtag
     half waits for gtag to exist (up to 30 s) instead of being skipped. */
  var gtagPending = [];
  var gtagWaiting = false;

  function flushGtag() {
    if (typeof window.gtag !== 'function') return false;
    while (gtagPending.length) {
      var p = gtagPending.shift();
      try { window.gtag('event', p.event, p); } catch (e) {}
    }
    return true;
  }

  function waitForGtag() {
    if (gtagWaiting) return;
    gtagWaiting = true;
    var n = 0;
    (function tick() {
      if (flushGtag() || ++n > 120) { gtagWaiting = false; return; }
      setTimeout(tick, 250);
    })();
  }

  function send(payload) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    } catch (e) {}
    try {
      gtagPending.push(payload);
      if (!flushGtag()) waitForGtag();
    } catch (e) {}
  }

  function emit(name, extra) {
    try {
      var payload = {
        event: name,
        ab_test: AB.test,
        ab_arm: AB.arm,
        ab_stable: AB.stable,
        ab_product_id: AB.product || null
      };
      if (extra) for (var k in extra) payload[k] = extra[k];

      if (consent === true) send(payload);
      else if (queue.length < MAX_QUEUE) queue.push(payload);
    } catch (e) {}
  }

  /* An exposure is seeing a gallery. Emitting on collection pages would
     inflate the SRM denominator, so this is gated on inTest, which only the
     gallery payload sets. Routed through markExposed so the product page and
     the quick-view count the same way. */
  if (AB.inTest && !swapFailed) AB.markExposed();

  /* Fired here as well as from markSwapFailed because on a product page the
     inline swap sets the plain property before this file exists. The view is
     NOT counted as an exposure (just above), so nothing has to be subtracted
     later: ab_noswap is a counter of failures, not a correction. */
  if (swapFailed) emitNoswap();

  /* Back from another tab, or restored from the back/forward cache: storage
     may say "seen" now even though this page loaded before it did. */
  function refreshSeen() {
    if (!seen && readSeen()) {
      seen = true;
      retag();
    }
  }
  try {
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') refreshSeen();
    });
    window.addEventListener('pageshow', refreshSeen);
    /* And at the last moment, for a page that never left view (two windows
       side by side). Capture phase, so it runs before the theme's own submit
       or click handler reads the form. A no-op once seen is true. */
    document.addEventListener('submit', refreshSeen, true);
    document.addEventListener('click', function (e) {
      try {
        if (e.target && e.target.closest && e.target.closest('form[action*="/cart/add"]')) refreshSeen();
      } catch (err) {}
    }, true);
  } catch (e) {}

  /* Observe /cart/add without altering it. Exposed visitors only: the
     funnel denominator is exposures. Not from a :noswap page view either:
     that view emitted no exposure, so its add-to-cart must not reach the
     numerator (it is also left out of the orders by its tag). */
  (function watchCartAdd() {
    try {
      var of = window.fetch;
      if (typeof of === 'function' && !of.__bfAb) {
        var wrapped = function (input) {
          try {
            var url = (typeof input === 'string') ? input : (input && input.url) || String(input || '');
            if (!swapFailed && isSeen() && /\/cart\/add/.test(url)) emit('ab_add_to_cart');
          } catch (e) {}
          return of.apply(this, arguments);
        };
        wrapped.__bfAb = true;
        window.fetch = wrapped;
      }

      var oo = XMLHttpRequest.prototype.open;
      if (!oo.__bfAb) {
        var wrappedOpen = function (method, url) {
          try {
            if (!swapFailed && isSeen() && /\/cart\/add/.test(String(url || ''))) emit('ab_add_to_cart');
          } catch (e) {}
          return oo.apply(this, arguments);
        };
        wrappedOpen.__bfAb = true;
        XMLHttpRequest.prototype.open = wrappedOpen;
      }
    } catch (e) {}
  })();

  /* --- 5. QA ------------------------------------------------------------- */

  AB.debug = function () {
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    var tagged = 0;
    forms.forEach(function (f) { if (f.querySelector('[name="' + FIELD + '"]')) tagged++; });
    return {
      test: AB.test,
      arm: AB.arm,
      stable: AB.stable,
      onTestProductPage: AB.inTest,
      everSawGallery: isSeen(),
      exposureCounted: exposed,
      swapFailed: swapFailed,
      imagesSwapped: AB.swapped === undefined ? '(arm A, no match, or not a test page)' : AB.swapped,
      formsTagged: tagged + '/' + forms.length,
      lineItemValue: value(),
      consent: consent === null ? 'undecided (queuing)' : consent,
      queuedEvents: queue.length,
      /* Renamed: this used to force the visitor into arm A. It now excludes
         the product from both arms instead, so "forcedToA" said the opposite
         of what happens. */
      excludedProduct: document.documentElement.getAttribute('data-ab-invalid') || false
    };
  };
})();